import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@stores/translationStore';
import PageHeader from '@components/PageHeader';
import PageSpinner from '@components/PageSpinner';
import {
  getPreCheckInState,
  savePreCheckInDetails,
  confirmPreCheckIn,
  type PreCheckInState,
} from '@services/precheckinApi';

type Step = 'details' | 'confirm' | 'done';

/**
 * Registration — the guest's details and signature before arrival. This was the pre-check-in form's
 * details + confirm steps; it is now a portal module on the stay's own session, so what the guest
 * enters here is the same record the front desk sees and check-in carries forward.
 */
export default function RegistrationPage() {
  const { hotelSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);

  const [state, setState] = useState<PreCheckInState | null>(null);
  const [step, setStep] = useState<Step>('details');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [passportId, setPassportId] = useState('');
  const [dob, setDob] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [babies, setBabies] = useState(0);
  const [signedName, setSignedName] = useState('');
  const [accepted, setAccepted] = useState(false);

  const fill = (s: PreCheckInState) => {
    setState(s);
    setFirstName(s.guestFirstName || '');
    setLastName(s.guestLastName || '');
    setEmail(s.email || '');
    setPhone(s.phoneE164 || '');
    setCountry(s.country || '');
    setAdults(s.adults || 1);
    setChildren(s.children || 0);
    setBabies(s.babies || 0);
    setSignedName([s.guestFirstName, s.guestLastName].filter(Boolean).join(' '));
  };

  useEffect(() => {
    getPreCheckInState()
      .then((s) => {
        fill(s);
        if (s.status === 'Complete') setStep('done');
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const home = () => navigate(hotelSlug ? `/${hotelSlug}/welcome` : '/welcome');
  const toDocuments = () => navigate(hotelSlug ? `/${hotelSlug}/documents` : '/documents');

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError(tr('registration.nameRequired', 'Please enter your first and last name.'));
      return;
    }
    setBusy(true);
    try {
      const s = await savePreCheckInDetails({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phoneE164: phone.trim() || undefined,
        country: country.trim() || undefined,
        passportId: passportId.trim() || undefined,
        dateOfBirth: dob || null,
        adults,
        children,
        babies,
      });
      fill(s);
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('registration.saveFailed', 'Could not save your details.'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!accepted) {
      setError(tr('registration.acceptTerms', 'Please accept the terms to continue.'));
      return;
    }
    setBusy(true);
    try {
      const s = await confirmPreCheckIn(true, signedName.trim() || undefined);
      setState(s);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('registration.confirmFailed', 'Could not complete your registration.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageSpinner />;

  const backButton = (
    <button className="btn btn-link text-white p-0" onClick={step === 'confirm' ? () => setStep('details') : home}
      aria-label={tr('common.back', 'Back')}>
      ←
    </button>
  );

  return (
    <div className="page-content">
      <PageHeader
        title={tr('registration.title', 'Registration')}
        subtitle={tr('registration.subtitle', 'Your details for the stay')}
        leading={backButton}
      />

      <div className="py-3">
        {error && <div className="alert alert-danger">{error}</div>}

        {step === 'details' && (
          <form onSubmit={saveDetails}>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small">{tr('registration.firstName', 'First name')} *</label>
                <input className="form-control" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="form-label small">{tr('registration.lastName', 'Last name')} *</label>
                <input className="form-control" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <label className="form-label small mt-2">{tr('registration.email', 'Email')}</label>
            <input className="form-control" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label className="form-label small mt-2">{tr('registration.phone', 'Phone')}</label>
            <input className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+972..." />
            <div className="row g-2 mt-0">
              <div className="col-6">
                <label className="form-label small mt-2">{tr('registration.country', 'Country')}</label>
                <input className="form-control" value={country} onChange={(e) => setCountry(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="form-label small mt-2">{tr('registration.passportId', 'Passport / ID')}</label>
                <input className="form-control" value={passportId} onChange={(e) => setPassportId(e.target.value)} />
              </div>
            </div>
            <label className="form-label small mt-2">{tr('registration.dateOfBirth', 'Date of birth')}</label>
            <input className="form-control" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />

            <div className="small fw-semibold mt-3">{tr('registration.party', 'Guests')}</div>
            <div className="row g-2">
              <div className="col-4">
                <label className="form-label small">{tr('registration.adults', 'Adults')}</label>
                <input className="form-control" type="number" min={1} value={adults} onChange={(e) => setAdults(Number(e.target.value))} />
              </div>
              <div className="col-4">
                <label className="form-label small">{tr('registration.children', 'Children')}</label>
                <input className="form-control" type="number" min={0} value={children} onChange={(e) => setChildren(Number(e.target.value))} />
              </div>
              <div className="col-4">
                <label className="form-label small">{tr('registration.babies', 'Babies')}</label>
                <input className="form-control" type="number" min={0} value={babies} onChange={(e) => setBabies(Number(e.target.value))} />
              </div>
            </div>

            <button type="submit" className="btn btn-primary w-100 mt-4" disabled={busy}>
              {busy ? tr('common.saving', 'Saving…') : tr('common.continue', 'Continue')}
            </button>
          </form>
        )}

        {step === 'confirm' && state && (
          <form onSubmit={confirm}>
            <div className="card mb-3">
              <ul className="list-group list-group-flush">
                <Row label={tr('registration.name', 'Name')} value={[state.guestFirstName, state.guestLastName].filter(Boolean).join(' ')} />
                <Row label={tr('registration.email', 'Email')} value={state.email} />
                <Row label={tr('registration.phone', 'Phone')} value={state.phoneE164} />
                <Row label={tr('registration.country', 'Country')} value={state.country} />
                <Row label={tr('registration.party', 'Guests')}
                  value={`${state.adults} + ${state.children} + ${state.babies}`} />
              </ul>
            </div>

            <label className="form-label small">{tr('registration.signature', 'Signature (type your full name)')}</label>
            <input className="form-control" value={signedName} onChange={(e) => setSignedName(e.target.value)} />

            <div className="form-check mt-3">
              <input className="form-check-input" type="checkbox" id="accept" checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)} />
              <label className="form-check-label small" htmlFor="accept">
                {tr('registration.terms', "I confirm the details above are correct and I accept the property's terms and conditions.")}
              </label>
            </div>

            <button type="submit" className="btn btn-primary w-100 mt-4" disabled={busy}>
              {busy ? tr('common.saving', 'Saving…') : tr('registration.complete', 'Complete registration')}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <div style={{ fontSize: '3rem' }}>✓</div>
            <h3 className="h5">{tr('registration.doneTitle', "You're all set")}</h3>
            <p className="text-muted">
              {tr('registration.doneText', "Your registration is complete. We'll have everything ready for your arrival.")}
            </p>
            <button className="btn btn-outline-primary w-100 mb-2" onClick={toDocuments}>
              {tr('registration.addDocuments', 'Upload documents')}
            </button>
            <button className="btn btn-link w-100" onClick={() => setStep('details')}>
              {tr('registration.edit', 'Edit my details')}
            </button>
            <button className="btn btn-primary w-100 mt-2" onClick={home}>
              {tr('common.backHome', 'Back to home')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <li className="list-group-item d-flex justify-content-between">
      <span className="text-muted small">{label}</span>
      <span className="fw-semibold text-end">{value || '—'}</span>
    </li>
  );
}
